// src/pages/AgreementsPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const AgreementsPage = () => {
  const navigate = useNavigate();
  const [requestedItems, setRequestedItems] = useState({});
  const [packageRequested, setPackageRequested] = useState(false);

  const handleGetAgreement = (id, title, price) => {
    setRequestedItems(prev => ({
      ...prev,
      [id]: true
    }));
    // Navigate to inquiry page with agreement details
    navigate('/inquiry', { 
      state: { 
        agreementType: title,
        agreementPrice: price,
        isPackage: false 
      }
    });
  };

  const handleGetBundle = () => {
    setPackageRequested(true);
    // Navigate to inquiry page with bundle details
    navigate('/inquiry', { 
      state: { 
        agreementType: 'Complete Transaction Bundle',
        agreementPrice: '₹25,000',
        isPackage: true 
      }
    });
  };

  const handleCustomAgreement = () => {
    navigate('/inquiry', { 
      state: { 
        agreementType: 'Custom Agreement',
        agreementPrice: 'Custom Quote',
        isPackage: false 
      }
    });
  };

  const agreements = [
    {
      id: 'rent',
      title: 'Rent Agreement',
      tag: 'RESIDENTIAL',
      tagColor: '#EEEDFE',
      tagTextColor: '#3C3489',
      topBarColor: '#7F77DD',
      icon: '🏠',
      iconBg: '#EEEDFE',
      iconColor: '#534AB7',
      description: 'Standard leave & licence agreement for residential or commercial rentals. Covers tenure, notice period, security deposit, and clauses.',
      price: '₹999',
      note: '+ Govt. registration charges'
    },
    {
      id: 'mou',
      title: 'MOU — Memorandum of Understanding',
      tag: 'MEMORANDUM',
      tagColor: '#E1F5EE',
      tagTextColor: '#085041',
      topBarColor: '#1D9E75',
      icon: '🤝',
      iconBg: '#E1F5EE',
      iconColor: '#0F6E56',
      description: 'Non-binding preliminary agreement outlining intent between buyer and seller. Protects both parties during negotiation and due diligence.',
      price: '₹2,000',
      note: 'Flat rate, no hidden charges'
    },
    {
      id: 'sale',
      title: 'Agreement to Sale',
      tag: 'SALE',
      tagColor: '#FAECE7',
      tagTextColor: '#712B13',
      topBarColor: '#D85A30',
      icon: '🏢',
      iconBg: '#FAECE7',
      iconColor: '#993C1D',
      description: 'Pre-sale contract binding both parties to complete the transaction. Specifies payment schedule, possession date, and penalty clauses.',
      price: '₹12,000',
      note: '+ Govt. registration charges'
    },
    {
      id: 'assignment',
      title: 'Deed of Assignment',
      tag: 'TRANSFER',
      tagColor: '#E6F1FB',
      tagTextColor: '#0C447C',
      topBarColor: '#378ADD',
      icon: '↗️',
      iconBg: '#E6F1FB',
      iconColor: '#185FA5',
      description: 'Legal instrument transferring ownership rights from assignor to assignee. Required for resale of under-construction or leasehold properties.',
      price: '₹12,000',
      note: '+ Govt. registration charges'
    }
  ];

  const packageFeatures = [
    'MOU — Memorandum of Understanding',
    'Agreement to Sale',
    'Deed of Assignment',
    'Priority review & delivery within 24h',
    'One free revision per document'
  ];

  return (
    <div style={{
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      backgroundColor: '#ffffff',
      color: '#1a1a1a',
      padding: '48px 24px 72px',
      width: '100%',
      minHeight: '100vh',
      boxSizing: 'border-box',
      margin: 0
    }}>
      {/* Inner container for content */}
      <div style={{
        maxWidth: '1080px',
        margin: '0 auto'
      }}>
        {/* Screen reader only heading */}
        <h2 style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: '0',
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          border: '0'
        }}>
          Property agreements and pricing — rent agreements, MOUs, sale deeds, and packages
        </h2>

        {/* Eyebrow */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          border: '0.5px solid #e0e0e0',
          borderRadius: '999px',
          padding: '5px 14px',
          fontSize: '12px',
          fontWeight: 500,
          color: '#666666',
          letterSpacing: '0.06em',
          marginBottom: '24px'
        }}>
          <span>✅</span>
          LEGALLY VETTED DOCUMENTS
        </div>

        {/* Hero Section */}
        <h1 style={{
          fontSize: 'clamp(32px, 5vw, 54px)',
          fontWeight: 500,
          color: '#1a1a1a',
          lineHeight: 1.1,
          marginBottom: '14px'
        }}>
          Property <span style={{ color: '#534AB7' }}>agreements</span>
          <br />done right.
        </h1>
        <p style={{
          fontSize: '16px',
          color: '#666666',
          lineHeight: 1.7,
          maxWidth: '480px',
          marginBottom: '36px'
        }}>
          Professionally drafted, legally reviewed templates for every stage of your property transaction — rental to commercial.
        </p>

        {/* Stats Row */}
        <div style={{
          display: 'flex',
          gap: '40px',
          flexWrap: 'wrap',
          marginBottom: '56px',
          paddingBottom: '40px',
          borderBottom: '0.5px solid #e8e8e8'
        }}>
          {[
            { value: '500+', label: 'DOCUMENTS ISSUED' },
            { value: '100%', label: 'LEGALLY VETTED' },
            { value: '< 24h', label: 'DELIVERY TIME' },
            { value: '4.9★', label: 'CLIENT RATING' }
          ].map((stat, index) => (
            <div key={index} style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '3px'
            }}>
              <span style={{
                fontSize: '22px',
                fontWeight: 500,
                color: '#1a1a1a'
              }}>{stat.value}</span>
              <span style={{
                fontSize: '12px',
                color: '#666666',
                letterSpacing: '0.04em'
              }}>{stat.label}</span>
            </div>
          ))}
        </div>

        {/* Section Label */}
        <p style={{
          fontSize: '11px',
          fontWeight: 500,
          color: '#999999',
          letterSpacing: '0.1em',
          marginBottom: '20px'
        }}>INDIVIDUAL AGREEMENTS</p>

        {/* Cards Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))',
          gap: '16px',
          marginBottom: '48px'
        }}>
          {agreements.map((agreement) => (
            <div
              key={agreement.id}
              style={{
                background: '#ffffff',
                border: '0.5px solid #e8e8e8',
                borderRadius: '12px',
                padding: '0',
                overflow: 'hidden',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                cursor: 'default'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#d0d0d0';
                e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,0,0,0.07)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e8e8e8';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Top Bar */}
              <div style={{
                height: '3px',
                width: '100%',
                background: agreement.topBarColor
              }} />

              {/* Card Body */}
              <div style={{ padding: '20px 20px 0' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    flexShrink: 0,
                    background: agreement.iconBg
                  }}>
                    {agreement.icon}
                  </div>
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 500,
                    letterSpacing: '0.06em',
                    padding: '3px 10px',
                    borderRadius: '999px',
                    background: agreement.tagColor,
                    color: agreement.tagTextColor
                  }}>{agreement.tag}</span>
                </div>

                <h3 style={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: '#1a1a1a',
                  marginBottom: '6px',
                  lineHeight: 1.3
                }}>{agreement.title}</h3>

                <p style={{
                  fontSize: '13px',
                  color: '#666666',
                  lineHeight: 1.6,
                  marginBottom: '20px'
                }}>{agreement.description}</p>
              </div>

              {/* Card Footer */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 20px',
                borderTop: '0.5px solid #e8e8e8',
                background: '#f9f9f9'
              }}>
                <div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: 500,
                    color: '#1a1a1a'
                  }}>{agreement.price}</div>
                  <div style={{
                    fontSize: '11px',
                    color: '#999999',
                    marginTop: '1px'
                  }}>{agreement.note}</div>
                </div>
                <button
                  onClick={() => handleGetAgreement(agreement.id, agreement.title, agreement.price)}
                  style={{
                    fontSize: '13px',
                    color: requestedItems[agreement.id] ? '#1a1a1a' : '#666666',
                    background: requestedItems[agreement.id] ? '#f0f0f0' : '#ffffff',
                    border: requestedItems[agreement.id] ? '0.5px solid #d0d0d0' : '0.5px solid #d0d0d0',
                    borderRadius: '8px',
                    padding: '7px 14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'background 0.15s, color 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    if (!requestedItems[agreement.id]) {
                      e.currentTarget.style.background = '#f5f5f5';
                      e.currentTarget.style.color = '#1a1a1a';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!requestedItems[agreement.id]) {
                      e.currentTarget.style.background = '#ffffff';
                      e.currentTarget.style.color = '#666666';
                    }
                  }}
                >
                  {requestedItems[agreement.id] ? (
                    <>
                      <span>✓</span> Requested
                    </>
                  ) : (
                    <>
                      <span>📄</span> Get agreement
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Package Card */}
        <div style={{
          borderRadius: '16px',
          border: '2px solid #534AB7',
          padding: '32px',
          marginBottom: '48px',
          position: 'relative',
          background: '#ffffff'
        }}>
          {/* Badge */}
          <span style={{
            position: 'absolute',
            top: '-12px',
            left: '32px',
            background: '#534AB7',
            color: '#EEEDFE',
            fontSize: '11px',
            fontWeight: 500,
            letterSpacing: '0.08em',
            padding: '4px 14px',
            borderRadius: '999px'
          }}>BEST VALUE PACKAGE</span>

          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '16px',
            flexWrap: 'wrap'
          }}>
            <div style={{
              flex: 1,
              minWidth: '240px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '10px'
              }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  background: '#EEEDFE',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <span style={{ fontSize: '22px' }}>📦</span>
                </div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 500,
                  color: '#1a1a1a',
                  margin: 0
                }}>Complete Transaction Bundle</h3>
              </div>

              <p style={{
                fontSize: '14px',
                color: '#666666',
                marginBottom: '24px',
                lineHeight: 1.6
              }}>
                Everything you need from intent to ownership — MOU, Agreement to Sale, and Deed of Assignment drafted as a cohesive set by our legal team.
              </p>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                marginBottom: '24px'
              }}>
                {packageFeatures.map((feature, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '13px',
                    color: '#666666'
                  }}>
                    <span style={{ color: '#534AB7', fontSize: '16px' }}>✓</span>
                    {feature}
                  </div>
                ))}
              </div>
            </div>

            {/* Package Bottom */}
            <div style={{
              alignSelf: 'flex-end',
              width: '100%',
              paddingTop: '20px',
              borderTop: '0.5px solid #e8e8e8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '8px',
                  marginBottom: '4px'
                }}>
                  <span style={{
                    fontSize: '13px',
                    color: '#999999',
                    textDecoration: 'line-through'
                  }}>₹26,000</span>
                  <span style={{
                    fontSize: '12px',
                    color: '#3B6D11',
                    background: '#EAF3DE',
                    borderRadius: '999px',
                    padding: '3px 10px',
                    fontWeight: 500
                  }}>Save ₹1,000</span>
                </div>
                <div style={{
                  fontSize: '30px',
                  fontWeight: 500,
                  color: '#1a1a1a'
                }}>₹25,000</div>
                <div style={{
                  fontSize: '12px',
                  color: '#999999',
                  marginTop: '3px'
                }}>Govt. charges extra where applicable</div>
              </div>

              <button
                onClick={handleGetBundle}
                style={{
                  background: packageRequested ? '#3C3489' : '#534AB7',
                  color: '#EEEDFE',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 28px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!packageRequested) {
                    e.currentTarget.style.background = '#3C3489';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!packageRequested) {
                    e.currentTarget.style.background = '#534AB7';
                  }
                }}
              >
                {packageRequested ? (
                  <>
                    <span>✓</span> Package requested
                  </>
                ) : (
                  <>
                    <span>📦</span>
                    Get the bundle
                    <span>→</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Custom Agreement Banner */}
        <div style={{
          background: '#f9f9f9',
          border: '0.5px solid #e8e8e8',
          borderRadius: '12px',
          padding: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '24px',
          flexWrap: 'wrap'
        }}>
          <div>
            <div style={{
              fontSize: '28px',
              color: '#534AB7',
              marginBottom: '12px'
            }}>⚖️</div>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 500,
              color: '#1a1a1a',
              marginBottom: '6px'
            }}>Need a custom agreement?</h3>
            <p style={{
              fontSize: '14px',
              color: '#666666',
              lineHeight: 1.6,
              maxWidth: '420px'
            }}>
              Our legal experts draft bespoke property agreements tailored to your unique transaction — co-ownership, commercial leases, joint ventures, and more.
            </p>
          </div>
          <button
            onClick={handleCustomAgreement}
            style={{
              background: '#ffffff',
              border: '0.5px solid #d0d0d0',
              color: '#1a1a1a',
              borderRadius: '8px',
              padding: '11px 22px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              whiteSpace: 'nowrap',
              transition: 'border-color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#b0b0b0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#d0d0d0';
            }}
          >
            <span>✉️</span>
            Contact our legal team
            <span>→</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgreementsPage;